import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import {
  InfoDialogComponent,
  MESSAGE_ENUM,
} from '../../pages/dialogs/info-dialog/info-dialog.component';
import { ResultsColumnDialogComponent } from '../../pages/dialogs/results-column-dialog/results-column-dialog.component';
import { ValidationNoteDialogComponent } from '../../pages/dialogs/validation-note-dialog/validation-note-dialog.component';
import { XRefs } from '../xrefsDataModel';
import {
  DisplayableXrefColumns,
  DisplayPageSize,
  XrefColumnDisplayOrder,
  XrefColumnRename,
} from './xrefDisplayColumns';
import { Logging } from '../../utils/logging';

@Component({
  selector: 'app-xref-display-component',
  templateUrl: './xref-display.component.html',
  styleUrls: ['./xref-display.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class XrefDisplayComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  dataSource: MatTableDataSource<XRefs>;
  displayableXrefColumns: DisplayableXrefColumns;
  displayPageSize: DisplayPageSize;

  firstToColumnName: string;
  firstOtherColumnName: string;

  toColumnCount: number;
  fromColumnCount: number;
  otherColumnCount: number;
  validationToTrue: boolean = false;
  validationToFalse: boolean = false;

  currentFilter = '';
  displayedColumns: string[] = [];

  @Input() set xrefData(value: XRefs[]) {
    if (this.dataSource != null) {
      this.dataSource.data = value;
      this.currentFilter = '';
      this.dataSource.filter = '';
    }
  }
  get xrefData() {
    return this.dataSource.data;
  }
  @Input() set xrefColumnDisplayOrder(value: XrefColumnDisplayOrder[]) {
    this.displayableXrefColumns.setXrefDisplayColumnDefaults(value);
  }
  @Input() set xrefColumnRename(value: XrefColumnRename[]) {
    this.displayableXrefColumns.renameXrefColumns(value);
  }
  @Input() allowUpload: boolean = false;
  @Input() searching: boolean = false;
  @Input() hasSearched: boolean = false;
  @Input() loadError: boolean = false;
  @Input() canEdit: boolean = false;
  @Input() internalUser: boolean = false;
  @Input() allowColumnChanges: boolean = false;
  @Input() updatingXrefs: boolean = false;
  @Input() doubleHeader: boolean = false;
  @Input() set isUpdating(value: boolean) {
    if (!value) {
      this.validationToFalse = false;
      this.validationToTrue = false;
    }
  }
  @Input() singleExport: boolean = false;
  @Input() multiExport: boolean = false;
  @Input() exportButtonData: ExportData[] = [];
  @Input() paginationOptions: number[] = [25, 100, 250];
  @Input() paginationDefault: number = 250;
  @Input() maxDisplayedColumns: number = 18;
  @Input() activeExternalSort: string = 'fromPartNumber';
  @Input() activeInternalSort: string = 'fromPim';

  @Output() exportClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() saveChangesClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() cancelSaveClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() uploadClicked: EventEmitter<any> = new EventEmitter<any>();

  constructor(public dialog: MatDialog) {
    this.displayableXrefColumns = new DisplayableXrefColumns();
  }

  ngOnInit(): void {
    this.displayPageSize = new DisplayPageSize();
    this.paginationDefault =
      this.displayPageSize.loadPageSize() > 0
        ? this.displayPageSize.loadPageSize()
        : 250;
    this.displayedColumns =
      this.displayableXrefColumns.getXrefDisplayedColumnKeys(
        !this.internalUser,
      );

    this.toColumnCount = this.displayableXrefColumns.toColumnCount(
      !this.internalUser,
    );
    this.fromColumnCount = this.displayableXrefColumns.fromColumnCount(
      !this.internalUser,
    );
    this.otherColumnCount = this.displayableXrefColumns.otherColumnCount(
      !this.internalUser,
    );

    //#region set up the datagrid
    this.dataSource = new MatTableDataSource();
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = (data: XRefs, filter: string) => {
      if (!filter || filter == '') return true;
      // Create string with all the data from shown columns for the row.
      // Seperated by a non-typable character (the entry separator)
      let sString = '';
      try {
        this.displayableXrefColumns
          .getXrefDisplayedColumnKeys(!this.internalUser)
          .forEach(key => {
            if (this.displayableXrefColumns.XrefColumns[key].filterable) {
              sString = sString.concat(data[key], String.fromCharCode(30));
            }
          });
      } catch (e) {
        Logging.error(e);
      }
      // Look for the filter in the concatenated data
      return sString.toLowerCase().includes(filter.toLowerCase());
    };

    // Set the sorts to be case-insensitive
    this.dataSource.sortingDataAccessor = (item, property) => {
      try {
        if (property == 'confidenceScore') {
          if (item[property].toLowerCase() == 'high') return 4;
          if (item[property].toLowerCase() == 'medium') return 3;
          if (item[property].toLowerCase() == 'low') return 2;
          if (item[property].toLowerCase() == 'none') return 1;
        }
        //sort pims as numbers
        if (property == 'fromPim' || property == 'toPim') {
          return +item[property];
        }
        if (property == 'fromUNSPSC' || property == 'toUNSPSC') {
          if (this.internalUser) {
            return +item[property + 'Code'];
          } else {
            return item[property + 'Desc'].trim().toLowerCase();
          }
        }
        if (property == 'createDTM') {
          // For a date
          if (item[property] == null || item[property] == '') return 0;
          const date = item[property].split('/');
          const num = +date[2] * 10000 + +date[0] * 100 + +date[1];
          return num;
        }
        return item[property].trim().toLowerCase();
      } catch (e) {
        return item[property];
      }
    };
    this.dataSource.sort = this.sort;

    this.firstToColumnName = this.displayableXrefColumns.firstToColumnName(
      !this.internalUser,
    );
    this.firstOtherColumnName =
      this.displayableXrefColumns.firstOtherColumnName(!this.internalUser);
    //#endregion
  }

  changePageSize(pageSize: number) {
    this.displayPageSize.savePage(pageSize);
  }

  ActiveSort(): string {
    return this.internalUser
      ? this.activeInternalSort
      : this.activeExternalSort;
  }

  GetXrefColumnHeaderName(key: string) {
    return this.doubleHeader
      ? this.displayableXrefColumns.XrefColumns[key].columnHeaderName
      : this.displayableXrefColumns.XrefColumns[key].userReadableName;
  }

  ApplyFilterEvent(event: Event) {
    this.ApplyFilter((event.target as HTMLInputElement).value);
  }

  ApplyFilter(filterValue: string) {
    this.currentFilter = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  //#region dialogs
  catRankDialog(event: any) {
    this.dialog.open(InfoDialogComponent, {
      width: '700px',
      data: {
        messsage: this.internalUser
          ? MESSAGE_ENUM.CAT_RANK_INTERNAL
          : MESSAGE_ENUM.CAT_RANK_EXTERNAL,
      },
    });
    event.stopPropagation();
  }

  confidenceLabelDialog(event: any) {
    this.dialog.open(InfoDialogComponent, {
      width: '700px',
      data: {
        messsage: this.internalUser
          ? MESSAGE_ENUM.CONFIDENCE_LABEL_INTERNAL
          : MESSAGE_ENUM.CONFIDENCE_LABEL_EXTERNAL,
      },
    });
    event.stopPropagation();
  }

  showColumnDialog(): void {
    if (!this.allowColumnChanges) return;
    const dialogRef = this.dialog.open(ResultsColumnDialogComponent, {
      width: '1050px',
      data: {
        cols: this.displayableXrefColumns.getXrefAvailableColumns(
          !this.internalUser,
        ),
        maxCols: this.maxDisplayedColumns,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.displayableXrefColumns.saveDisplayedColumns();
        this.displayedColumns =
          this.displayableXrefColumns.getXrefDisplayedColumnKeys(
            !this.internalUser,
          );
        this.fromColumnCount = this.displayableXrefColumns.fromColumnCount(
          !this.internalUser,
        );
        this.toColumnCount = this.displayableXrefColumns.toColumnCount(
          !this.internalUser,
        );
        this.otherColumnCount = this.displayableXrefColumns.otherColumnCount(
          !this.internalUser,
        );

        this.firstToColumnName = this.displayableXrefColumns.firstToColumnName(
          !this.internalUser,
        );
        this.firstOtherColumnName =
          this.displayableXrefColumns.firstOtherColumnName(!this.internalUser);
      } else {
        this.displayableXrefColumns.loadDisplayedColumns();
      }
      this.ApplyFilter(this.currentFilter);
    });
  }

  validationNoteDialog(event: any, titleId: string, validationNote: string) {
    this.dialog.open(ValidationNoteDialogComponent, {
      width: '600px',
      data: { id: titleId, text: validationNote },
    });
    event.stopPropagation();
  }
  //#endregion

  updateColumnBorderClass(actual: string): string {
    if (
      this.doubleHeader &&
      (this.firstToColumnName == actual || this.firstOtherColumnName == actual)
    )
      return 'leftBorder';

    return '';
  }

  //#region export
  ExportData(id: number) {
    this.exportClicked.emit(id);
  }

  canExport(): boolean {
    return (this.singleExport || this.multiExport) && this.xrefData.length > 0;
  }
  //#endregion

  //#region Change of validation and reviewed
  disableValidCheckbox(element: XRefs): boolean {
    if (!this.canEdit) return true;
    if (element.isValid && this.validationToTrue) return true;
    if (!element.isValid && this.validationToFalse) return true;
    return false;
  }
  CanSaveData(): boolean {
    let d = this.xrefData.filter(d => {
      return (d.toBeReviewed && !d.isReviewed) || d.validationChange;
    });
    return d.length > 0;
  }

  ShowSelectAllReviewButton(): boolean {
    if (!this.canEdit) return false;
    let reviewable = this.xrefData.filter(d => {
      return !d.isReviewed;
    });
    return reviewable.length > 0;
  }

  SelectAllReviewed() {
    if (
      this.xrefData.filter(d => !d.isReviewed && !d.toBeReviewed).length > 0
    ) {
      this.xrefData.forEach(d => {
        if (!d.isReviewed) d.toBeReviewed = true;
      });
    } else {
      this.xrefData.forEach(d => {
        d.toBeReviewed = false;
      });
    }
  }

  reviewAllButtonText(): string {
    let result = '';
    if (this.xrefData.length == 0) return result;
    let readyForReviewCount = this.xrefData.filter(
      d => !d.toBeReviewed && !d.isReviewed,
    ).length;
    if (readyForReviewCount > 0) result = 'Select All';
    else result = 'Deselect All';
    return result;
  }

  GetReviewedTooltip(element: XRefs) {
    if (element.isReviewed) {
      return element.reviewedBy + ' - ' + element.reviewedDate;
    }
    return '';
  }

  disableReviewedCheckbox(element: XRefs): boolean {
    if (element.isReviewed == null) return false;
    return element.isReviewed || !this.canEdit;
  }

  ChangeOfValidation(row: XRefs) {
    row.validationChange = !row.validationChange;
    if (!row.isReviewed) {
      row.toBeReviewed = true;
    }

    // TODO There must be a more efficient way to do this (i.e. toBeReviewedCount)
    this.validationToFalse = false;
    this.validationToTrue = false;
    this.dataSource.data.forEach(d => {
      if (d.validationChange) {
        this.validationToFalse = d.isValid;
        this.validationToTrue = !d.isValid;
      }
    });
  }

  CancelChangeOfValidation() {
    this.xrefData.forEach(d => {
      d.validationChange = false;
      d.toBeReviewed = false;
      d.validationCheckbox = d.isValid;
    });
    this.validationToFalse = false;
    this.validationToTrue = false;
  }

  SaveChanges() {
    if (this.canEdit) {
      this.saveChangesClicked.emit();
    }
  }

  UploadButtonClick() {
    if (this.canEdit) {
      this.uploadClicked.emit();
    }
  }
  //#endregion
}

export class ExportData {
  name: string;
  id: number;
}
